import { useState, useEffect } from "react";
import { defaultStartFor, isSingleDayMode, todayKey } from "../../utils/defaultRange";

import { ImpressionsChart } from "../../components/charts3";
import { ChartControls } from "../../components/Channel";

import { useDaysSorted } from "../../hooks/useDaysSorted";
import { useSwitchInSeries } from "../../hooks/useSwitchInSeries";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";

/**
 * Switch-In allowed modes
 */
const SWITCHIN_MODES = ["minute", "hour", "day", "week", "month"];

export default function ChannelSwitchIn({ channelId, rawImpressions, loading, refreshKey,refreshing, refreshEnabled, setRefreshEnabled, refreshIntervalMin, setRefreshIntervalMin, refreshCountdown, manualRefresh }) {
  const storedUser = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
  const defaultMode = storedUser.chartdefault || "minute";
  
  const [mode, setMode] = useState(defaultMode);

  // --- LOGGER INTEGRATION ---
  const { logPageChange, logTimeframeChange } = useUserJourneyLogger({
    channelId,
    // channelName is not available here, so omit or set to undefined
  });

  useEffect(() => {
    logPageChange({
      chartType: "switchin",
    });
  }, [logPageChange]);

  // Only log timeframe change on user-initiated mode change
  const handleModeChange = (newMode) => {
    setMode(newMode);
    setStartDateInput(defaultStartFor(newMode, endDateInput));
    logTimeframeChange({
      chartType: "switchin",
      mode: newMode,
    });
  };

  // Time range (minute only – harmless for others)
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");

  // Independent date state for this chart
  const [endDateInput, setEndDateInput] = useState(todayKey);
  const [startDateInput, setStartDateInput] = useState(() =>
    defaultStartFor(defaultMode, todayKey())
  );

  // The single-day picker reports only a start, so the end follows it there.
  const handleStartDateChange = (val) => {
    setStartDateInput(val);
    if (isSingleDayMode(mode)) setEndDateInput(val);
  };


  // rawImpressions and loading are now received as props
  const sortedKeys = useDaysSorted(rawImpressions);

  const series = useSwitchInSeries({
    rawImpressions,
    sortedImpKeys: sortedKeys,
    startDateInput,
    endDateInput,
    mode,
    startTime,
    endTime,
  });

  const showChart = !loading;

  return (
    <>
      <ChartControls
        channelId={channelId}
        mode={mode}
        onModeChange={handleModeChange}
        allowedModes={SWITCHIN_MODES}
        startDateInput={startDateInput}
        endDateInput={endDateInput}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={setEndDateInput}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
        refreshEnabled={refreshEnabled}
        onRefreshToggle={setRefreshEnabled}
        refreshIntervalMin={refreshIntervalMin}
        onRefreshIntervalChange={setRefreshIntervalMin}
        countdown={refreshCountdown}
        onManualRefresh={manualRefresh}
      />

      {showChart ? (
        <ImpressionsChart
          series={series}
          mode={mode}
          startTime={startTime}
          endTime={endTime}
          refreshKey={refreshKey}
        />  
        ) : (
        <div className="chart-container">
          <h3 className="chart-title">Switch-In (Impressions)</h3>
          <div className="chart-loading">
            <div className="chart-skeleton" />
            <div className="chart-loading-text">Loading chart...</div>
          </div>
        </div>
      )}
    </>
  );
}
